import { NuxtRoutes } from '../constants/routes'

/** Named routes that logged-out users may open. */
export const PUBLIC_AUTH_ROUTE_NAMES: readonly string[] = [
  NuxtRoutes.AuthLogin,
  NuxtRoutes.AuthSignup,
  NuxtRoutes.AuthMfaChallenge,
  NuxtRoutes.AuthForgotPassword,
  NuxtRoutes.AuthForgotPasswordAction,
  NuxtRoutes.AuthCustomActionRouter,
  NuxtRoutes.Unsubscribe,
]

/** Firebase email-action handler: always reachable, even while signed in. */
export const ALWAYS_PERMITTED_ROUTE_NAMES: readonly string[] = [
  NuxtRoutes.AuthCustomActionRouter,
]

/**
 * Static paths for those public routes. Kept as an explicit list because
 * `nuxt generate` + crawlLinks never discovers invite-only URLs such as
 * `/auth/signup`. Also used as `nitro.prerender.routes`.
 */
export const PUBLIC_AUTH_PATHS = [
  '/auth/login',
  '/auth/signup',
  '/auth/mfa-challenge',
  '/auth/forgotPassword',
  '/auth/forgotPasswordAction',
  '/auth/customActionRouter',
  '/unsubscribe',
] as const

export function canonicalRouteName(name: unknown): string {
  if (typeof name !== 'string' && typeof name !== 'symbol') {
    return ''
  }
  const value = String(name)
  const separator = value.lastIndexOf('___')
  return separator === -1 ? value : value.slice(0, separator)
}

export function canonicalPath(path: string): string {
  if (!path || path === '/') {
    return path || '/'
  }
  return path.replace(/\/+$/, '')
}

/** Collapse `/Home` (any case) onto `/home`. Returns null when the path is already canonical. */
export function canonicalizeHomePath(path: string): string | null {
  const normalized = canonicalPath(path)
  if (normalized.toLowerCase() !== '/home' || normalized === '/home') {
    return null
  }
  return '/home'
}

export function isPublicAuthRoute(to: { name?: unknown, path?: string }): boolean {
  const name = canonicalRouteName(to.name)
  if (name && PUBLIC_AUTH_ROUTE_NAMES.includes(name)) {
    return true
  }
  const path = to.path ? canonicalPath(to.path) : ''
  return (PUBLIC_AUTH_PATHS as readonly string[]).includes(path)
}

export function isAlwaysPermittedRoute(to: { name?: unknown, path?: string }): boolean {
  const name = canonicalRouteName(to.name)
  if (name && ALWAYS_PERMITTED_ROUTE_NAMES.includes(name)) {
    return true
  }
  return canonicalPath(to.path ?? '') === '/auth/customActionRouter'
}
